/**
 * SAYFA BAŞLIKLARI — tarayıcı sekmesinde ne yazacağı.
 *
 * Önceden her sayfa aynı sabit başlığı taşıyordu ("Audex Portal — Dijital
 * Denetim Portalı"): on sekme açıkken hangisinin hangi ekran olduğu
 * anlaşılmıyor, tarayıcı geçmişi ve yer imleri de tek isimle doluyordu.
 *
 * Eşleme burada, sayfaların içinde değil: yirmi kadar sayfaya tek tek başlık
 * yazmak, yeni sayfa eklendiğinde unutulacak bir iş demek. Kayda değer bir
 * varlık gösteren ekranlar (bir kontrol listesi, bir müşteri) kendi adını
 * `useDocumentTitle` ile ayrıca yazar.
 */

export const APP_NAME = "Audex"

/**
 * Yol kalıbı → başlık. `:param` herhangi bir tek segmenti karşılar.
 *
 * Sıra ÖNEMLİ: daha uzun/özel kalıplar önce geliyor, çünkü ilk eşleşen
 * kazanıyor — `/faaliyetler/yeni`, `/faaliyetler/:id`'den önce denenmeli.
 */
const ROUTE_TITLES: [pattern: string, title: string][] = [
  ["/", "Panel"],
  ["/giris", "Giriş"],
  ["/kayit", "Kayıt"],
  ["/sifremi-unuttum", "Şifremi Unuttum"],
  ["/f/:formId", "Form"],

  ["/ayarlar/profilim/sozlesmeler", "Sözleşmelerim"],
  ["/ayarlar/profilim/gorevler", "Görevlerim"],
  ["/ayarlar/profilim/evraklar", "Evraklarım"],
  ["/ayarlar/profilim/egitimler", "Eğitimlerim"],
  ["/ayarlar/profilim/zimmetler", "Zimmetlerim"],
  ["/ayarlar/profilim/izinler", "İzinlerim"],
  ["/ayarlar/profilim/masraflar", "Masraflarım"],
  ["/ayarlar/profilim/kayitlarim", "Kayıtlarım"],
  ["/ayarlar/profilim/talepler", "Taleplerim"],
  ["/ayarlar/profilim/loglar", "Loglarım"],
  ["/ayarlar/profilim", "Profilim"],
  ["/ayarlar", "Ayarlar"],

  ["/kullanici-yonetimi", "Kullanıcı Yönetimi"],
  ["/dil-yonetimi", "Dil Yönetimi"],
  ["/sistem-loglari", "Sistem Logları"],
  ["/sirket-yonetimi", "Şirket Yönetimi"],

  ["/denetim-setleri/:id", "Denetim Seti"],
  ["/denetim-setleri", "Denetim Setleri"],
  ["/sirket-sablonlari/:id/tasarim", "Şablon Tasarımı"],
  ["/sirket-sablonlari", "Şirket Şablonları"],

  ["/tasarim/dokumanlar/:id", "Doküman"],
  ["/tasarim/dokumanlar", "Dokümanlar"],
  ["/tasarim/alanlar", "Alanlar"],
  ["/dokuman-kategorileri", "Doküman Kategorileri"],
  ["/dokumantasyon/cop-kutusu", "Çöp Kutusu"],
  ["/dokumantasyon/:id", "Doküman"],
  ["/dokumantasyon", "Dokümantasyon"],
  ["/cop-kutusu", "Çöp Kutusu"],

  ["/kapsamlar", "Kapsamlar"],
  ["/meta-alan-tanimlari", "Meta Alan Tanımları"],
  ["/formlar/:key/yerlesim", "Yerleşim Tasarımı"],
  ["/formlar/:key", "Form"],
  // Kenar çubuğunda ve sayfa başlığında adı "Formlar"; sekme adı da öyle olsun.
  ["/formlar", "Formlar"],

  // KAPAKLAR — içerik tipinin ikinci türü, aynı ekranlar farklı rota.
  ["/kapaklar/:key/yerlesim", "Kapak Yerleşimi"],
  ["/kapaklar/:key", "Kapak"],
  ["/kapaklar", "Kapaklar"],

  ["/portal/dosyalar/:id", "Dosya"],
  ["/portal", "Portal"],

  ["/musteriler/:id", "Müşteri"],
  ["/musteriler", "Müşteriler"],
  ["/bildirimler", "Bildirimler"],
  ["/kayitlar/:key/yeni", "Yeni Kayıt"],
  ["/kayitlar/kayit/:id", "Kayıt"],
  ["/kayitlar", "Kayıtlar"],
  ["/kontrol-listeleri/:id", "Kontrol Listesi"],
  ["/kontrol-listeleri", "Kontrol Listeleri"],

  ["/faaliyetler/yeni", "Yeni Faaliyet Dosyası"],
  ["/faaliyetler/:id/duzenle", "Faaliyet Dosyası Düzenle"],
  ["/faaliyetler/:id/oturum/:sessionId/yazdir", "Yazdır"],
  ["/faaliyetler/:id/oturum/:sessionId", "Denetim Oturumu"],
  ["/faaliyetler/:id", "Faaliyet Dosyası"],
  ["/faaliyetler", "Faaliyet Dosyaları"],
  ["/personeller", "Personel Yönetimi"],
  ["/gorevlerim", "Görevlerim"],
  ["/raporlar", "Raporlar"],
  ["/cihaz-yonetimi", "Cihaz Yönetimi"],
  ["/yardim", "Yardım"],
]

/** Kalıp yola uyuyor mu? `:param` tek segmenti karşılar. */
function matches(pattern: string, path: string): boolean {
  const p = pattern.split("/").filter(Boolean)
  const q = path.split("/").filter(Boolean)
  if (p.length !== q.length) return false
  return p.every((seg, i) => seg.startsWith(":") || seg === q[i])
}

/** Yolun başlığı; eşleşme yoksa null (çağıran uygulama adına düşer). */
export function titleForPath(path: string): string | null {
  const clean = path.split("?")[0].split("#")[0]
  const hit = ROUTE_TITLES.find(([pattern]) => matches(pattern, clean))
  return hit ? hit[1] : null
}

/**
 * Sekmede görünecek tam metin.
 *
 * Ayırt edici parça ÖNDE: sekme daraldığında tarayıcı sonunu kırpıyor, marka
 * öne alınsaydı her sekme "Audex —" diye görünürdü.
 */
export function documentTitle(title?: string | null): string {
  const t = title?.trim()
  return t ? `${t} · ${APP_NAME}` : APP_NAME
}
